import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

type PublicTeacherChangeDetails = {
  id: number;
  status: string;
  summary?: any;
};

export default function TeacherChangeOffer() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<PublicTeacherChangeDetails | null>(null);

  const canAccept = useMemo(() => {
    const s = details?.status;
    return s === 'versendet' || s === 'angelegt';
  }, [details?.status]);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const d = await api.get<PublicTeacherChangeDetails>(`/public/teacher-change-orders/details?token=${encodeURIComponent(token)}`);
      setDetails(d);
    } catch (e: any) {
      toast.error(e?.message || 'Ungültiger oder abgelaufener Link');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const accept = async () => {
    setLoading(true);
    try {
      await api.post(`/public/teacher-change-orders/accept?token=${encodeURIComponent(token)}`, {});
      toast.success('Angebot angenommen');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Annehmen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const decline = async () => {
    setLoading(true);
    try {
      await api.post(`/public/teacher-change-orders/decline?token=${encodeURIComponent(token)}`, {});
      toast.success('Angebot abgelehnt');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Ablehnen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>Lehrerwechsel – Auftragsangebot</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!token ? (
            <div className="text-sm text-muted-foreground">Kein Token angegeben.</div>
          ) : loading && !details ? (
            <div className="text-sm text-muted-foreground">Lade…</div>
          ) : !details ? (
            <div className="text-sm text-muted-foreground">Keine Details verfügbar.</div>
          ) : (
            <>
              <div className="text-sm">
                <div className="text-muted-foreground text-xs mb-1">Status</div>
                <div className="font-medium">{details.status}</div>
              </div>

              {details.summary ? (
                <div className="rounded-md border bg-background p-3 text-sm">
                  <pre className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                    {JSON.stringify(details.summary, null, 2)}
                  </pre>
                </div>
              ) : null}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={load} disabled={loading}>
                  Aktualisieren
                </Button>
                <Button variant="destructive" onClick={decline} disabled={loading || !canAccept}>
                  Ablehnen
                </Button>
                <Button onClick={accept} disabled={loading || !canAccept}>
                  Annehmen
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


